import DOMPurify from 'dompurify'
import { editorConfig, excelFileType } from '../configs/constants'
import { saveAs } from 'file-saver'
import * as XLSX from 'xlsx'
import { getConfig } from '../configs/config'

// ** Checks if an object is empty (returns boolean)
export const isObjEmpty = (obj) => Object.keys(obj).length === 0

// ** Returns K format from a number
export const kFormatter = (num) => (num > 999 ? `${(num / 1000).toFixed(1)}k` : num)

// ** Converts HTML to string
export const htmlToString = (html) => html.replace(/<\/?[^>]+(>|$)/g, '')

// ** Locale-aware, case-insensitive string comparator for use as a sort/sortFunction comparator.
// JS's default sort (and `<`/`>`) is ordinal - it compares raw UTF-16 code units, so it's
// case-sensitive ("Zebra" sorts before "apple") and not locale-correct. `Intl.Collator` is the
// built-in API for locale-aware comparisons; it's used here (rather than calling
// `a.localeCompare(b, ...)` inline) because it compiles the collation rules once and reuses them,
// which is faster when sorting many rows.
const caseInsensitiveCollator = new Intl.Collator(undefined, { sensitivity: 'base' })
export const compareText = (a, b) => caseInsensitiveCollator.compare(a || '', b || '')

// ** Compares two date values by actual timestamp (ascending). Comparing date strings directly
// (`<`/`>`/localeCompare) only works if every value uses the exact same string format, so this
// converts to a timestamp first - the closest thing JS has to a built-in date comparator.
export const compareDate = (a, b) => new Date(a).getTime() - new Date(b).getTime()

// ** Checks if the passed date is today
const isToday = (date) => {
  const today = new Date()
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  )
}

/**
 ** Format and return date in Humanize format
 ** Intl docs: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/format
 ** Intl Constructor: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat
 * @param {String} value date to format
 * @param {Object} formatting Intl object to format with
 */
export const formatDate = (
  value,
  formatting = { month: 'short', day: 'numeric', year: 'numeric' }
) => {
  if (!value) return value
  return new Intl.DateTimeFormat('en-US', formatting).format(new Date(value))
}

// ** Returns short month of passed date
export const formatDateToMonthShort = (value, toTimeForCurrentDay = true) => {
  const date = new Date(value)
  let formatting = { month: 'short', day: 'numeric' }

  if (toTimeForCurrentDay && isToday(date)) {
    formatting = { hour: 'numeric', minute: 'numeric' }
  }

  return new Intl.DateTimeFormat('en-US', formatting).format(new Date(value))
}

/**
 ** Return if user is logged in
 ** This is completely up to you and how you want to store the token in your frontend application
 *  ? e.g. If you are using cookies to store the application please update this function
 */
export const isUserLoggedIn = () => localStorage.getItem('auth')
export const getUserData = () => JSON.parse(localStorage.getItem('userInfoData'))

/**
 ** This function is used for demo purpose route navigation
 ** In real app you won't need this function because your app will navigate to same route for each users regardless of ability
 ** Please note role field is just for showing purpose it's not used by anything in frontend
 ** We are checking role just for ease
 * ? NOTE: If you have different pages to navigate based on user ability then this function can be useful. However, you need to update it.
 * @param {String} userRole Role of user
 */
export const getHomeRouteForLoggedInUser = (userRole) => {
  if (userRole === 'admin') return '/'
  if (userRole === 'client') return '/access-control'
  return '/login'
}

// ** React Select Theme Colors
export const selectThemeColors = (theme) => {
  // Get CSS custom property values
  const primaryColor =
    getComputedStyle(document.documentElement).getPropertyValue('--color-primary').trim() ||
    '#055a60'
  const primaryLightColor =
    getComputedStyle(document.documentElement).getPropertyValue('--color-primary-light').trim() ||
    '#055a6020'

  return {
    ...theme,
    colors: {
      ...theme.colors,
      primary25: primaryLightColor, // for option hover bg-color
      primary50: primaryLightColor, // for option focused/first click bg-color
      primary: primaryColor, // for selected option bg-color
      neutral10: primaryColor, // for tags bg-color
      neutral20:
        getComputedStyle(document.documentElement)
          .getPropertyValue('--input-border-neutral')
          .trim() || '#ededed', // for input border-color
      neutral30:
        getComputedStyle(document.documentElement)
          .getPropertyValue('--input-border-neutral')
          .trim() || '#ededed' // for input hover border-color
    }
  }
}

export const CompRepeater = ({ children, data }) => {
  return data ? data.map((itemData, i) => children({ ...itemData, key: i })) : null
}

export const getUserInitials = (displayName) => {
  return `${displayName?.split(',')[1]?.trim().substring(0, 1)}${displayName
    ?.split(',')[0]
    ?.substring(0, 1)}`
}

export const isValidEmail = (email) => /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email)

export const logoutUser = async (instance, account, onRedirectNavigate) => {
  await instance.logout({
    account,
    onRedirectNavigate
  })
}

export const canParseJSON = (text) => {
  try {
    JSON.parse(text)
    return true
  } catch (e) {
    return false
  }
}

export const sanitizedData = (htmlData) => ({
  __html: DOMPurify.sanitize(htmlData, editorConfig)
})

// Adjust the z-index of the topbar when the drawer backdrop is enabled
export const modifyTopbarStyle = (openSidebar, menuCollapsed) => {
  const topbar = document.getElementById('topbar')
  if (topbar) {
    if (openSidebar) {
      topbar.style.zIndex = 13
      if (menuCollapsed) topbar.style.width = ' calc(100vw - 81px)'
    } else {
      topbar.style.removeProperty('z-index')
      topbar.style.removeProperty('width')
    }
  }
}

export const downloadData = async (type, dataSet, fileName) => {
  const workSheet = XLSX.utils.aoa_to_sheet(dataSet)
  const workBook = {
    Sheets: { data: workSheet, cols: [] },
    SheetNames: ['data']
  }
  const buffer = XLSX.write(workBook, { bookType: type, type: 'array' })
  const fileData = new Blob([buffer], { type: excelFileType })
  saveAs(fileData, `${fileName}.${type}`)
}

export const getExcelFileAdapter = (event) => {
  const XlsxReader = XLSX.read(event.target?.result, {
    type: 'binary'
  })

  return XLSX.utils.sheet_to_json(XlsxReader.Sheets[XlsxReader.SheetNames[0]], {
    defval: '',
    raw: false,
    header: 1,
    blankRows: false
  })
}

export const getCsvFileAdapter = (event) => {
  const CsvReader = XLSX.read(event.target?.result, {
    type: 'string'
  })
  return XLSX.utils.sheet_to_json(CsvReader.Sheets[CsvReader.SheetNames[0]], {
    defval: '',
    raw: false,
    header: 1,
    blankRows: false
  })
}

//get initials for avatar
export const getInitials = (name) => {
  if (!name) return

  const namesArray = name
    .trim()
    .split(' ')
    .filter((word) => word.length > 0)
  const initials = namesArray
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('')

  return initials
}

export const containsPropertyStartingWith = (obj, keyword) => {
  for (const prop in obj) {
    if (prop.startsWith(keyword)) {
      return true
    }
  }
  return false
}

export const getAvatarDisplayUrl = (avatar) => {
  if (!avatar) return ''
  if (typeof avatar === 'string') return avatar
  if (avatar instanceof File) return URL.createObjectURL(avatar)
  return ''
}

export const isSmartDocsPublicTeam = (teamName) => teamName === 'Community'
